<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from '../../../../helpers/utilities';
	//Stores
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import {
		Alert,
		Button,
		Toggle,
		Checkbox,
		Input,
		Slider,
		Text,
		toast,
		SliderV2,
	} from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let device;
	export let test_setting = false;

	let id = real_id(device.id);
	const min = 10;
	const max = 100;
	let settings = readDeviceSettings(id);

	let brightness = settings?.[get_gen_2_component_channel_structure(device)]?.warmup?.brightness,
		enabled = settings?.[get_gen_2_component_channel_structure(device)]?.warmup?.enable,
		warmTime = settings?.[get_gen_2_component_channel_structure(device)]?.warmup?.time_ms;

	let cacheBrightness = brightness,
		cacheEnabled = enabled,
		cacheWarmTime = warmTime;

	let loading = false;

	$: brightnessInputData = getErrorOrInfoMessageForMinAndMax(brightness, min, max);
	$: timeInputData = getErrorOrInfoMessageForMinAndMax(warmTime, 20, 1000);

	const setWarmUpConfig = async () => {
		if (cacheBrightness === brightness && cacheEnabled === enabled && cacheWarmTime === warmTime) {
			return;
		}

		if (brightnessInputData?.error || timeInputData?.error) {
			return toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Invalid values' }),
			});
		}

		loading = true;

		const params = {
			id: device.channel,
			config: {
				warmup: {
					enable: enabled ? true : false,
					brightness: brightness,
					time_ms: warmTime,
				},
			},
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device, {
			success: enabled ? $format('warm_up_save_scc_toast') : $format('warm_up_disabled_toast'),
			danger: $format('error_global'),
		});
		if (response) {
			// Update local settings
			if (!settings[get_gen_2_component_channel_structure(device)]) {
				settings[get_gen_2_component_channel_structure(device)] = {};
			}
			settings[get_gen_2_component_channel_structure(device)].warmup_enable = enabled;
			settings[get_gen_2_component_channel_structure(device)].warmup_brightness = brightness;
			settings[get_gen_2_component_channel_structure(device)].warmup_time_ms = warmTime;
			devices_settings.update_devices_settings(id, settings);

			// Update cache
			cacheBrightness = brightness;
			cacheEnabled = enabled;
			cacheWarmTime = warmTime;
		} else {
			// Revert values on error
			brightness = cacheBrightness;
			enabled = cacheEnabled;
			warmTime = cacheWarmTime;
		}
		loading = false;
	};

	const handleToggle = () => {
		enabled = !enabled;
		if ($showNewLayout) {
			setWarmUpConfig();
		}
	};
	const a11yProps = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enabled) {
			stickyBottomAction.set({ onClick: setWarmUpConfig, loading, disabled: false });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Toggle size="m" class="mb-s-plus" checked={enabled} on:click={handleToggle} id="checkbox-9aw8mns">
		{$format('label_enable', { default: 'Enable' })}
	</Toggle>

	<Alert
		class={classMap({
			'mb-s-plus': !test_setting,
			'mb-s': test_setting,
		})}
		heading={$format('warm_up_description', {
			default:
				'Gradually increase brightness when turning on the light to avoid sudden brightness changes.',
		})}
	/>
	{#if test_setting}
		<Alert
			class="mb-s-plus"
			type="warning"
			heading={$format('title_test_setting', {
				default: 'This is a test setting.',
			})}
			isHeadingBold
			message={$format('description_test_setting', {
				default: 'You can see this because you are using a test user account.',
			})}
		/>
	{/if}
	{#if enabled}
		<SliderV2
			id="slider-14x78tp"
			{a11yProps}
			class="mb-s-plus"
			bind:value={brightness}
			disabled={!enabled}
			step={1}
			min={10}
			max={100}
			title={$format('warm_up_brightness_label', {
				default: 'Warm-up brightness',
			})}
			titleTypography="body-small"
			type="incremented"
			lineMarkers={[
				{ value: 10, showLabel: true },
				{ value: 100, showLabel: true },
			]}
		/>
		<Text styleType="body-small" class="mb-s">
			{$format('warm_up_time_label', {
				default: 'Warm-up time',
			})}
		</Text>
		<Input
			size="m"
			bind:value={warmTime}
			icon="fa-solid fa-clock"
			type={'number'}
			min={20}
			max={1000}
			disabled={!enabled}
			step={1}
			error={timeInputData.error}
			info={timeInputData.info ||
				$format('warm_up_time_set', { default: 'Warmup time (20-1000ms)' })}
			on:enter={setWarmUpConfig}
			id="input-qrmshk0"
			aria-label={$format('warm_up_time_set', {
				default: 'Warmup time (20-1000ms)',
			})}
		/>
	{/if}
{:else}
	<Alert
		class="mb-s"
		heading={$format('warm_up_description', {
			default:
				'Gradually increase brightness when turning on the light to avoid sudden brightness changes.',
		})}
	/>
	<Checkbox size="m" class="mb-s" checked={enabled} on:click={handleToggle} id="checkbox-9aw8mns">
		{$format('label_enable', { default: 'Enable' })}
	</Checkbox>

	<Text styleType="body-small" class="mb-xs">
		{$format('warm_up_brightness_set', { default: 'Warmup brightness(10-100%)' })}
	</Text>

	<Slider
		id="slider-14x78tp"
		{a11yProps}
		class="mb-s"
		bind:value={brightness}
		disabled={!enabled}
		step={1}
		min={10}
		max={100}
		title={$format('label_brightness', { default: 'Brightness' })}
		titleTypography="body-extra-small"
		type="simple"
	/>
	<Input
		size="m"
		class="mb-s"
		bind:value={warmTime}
		type={'number'}
		min={20}
		max={1000}
		disabled={!enabled}
		step={1}
		error={timeInputData.error}
		info={timeInputData.info || $format('warm_up_time_set', { default: 'Warmup time (20-1000ms)' })}
		on:enter={setWarmUpConfig}
		id="input-qrmshk0"
		aria-label={$format('warm_up_time_set', {
			default: 'Warmup time (20-1000ms)',
		})}
	/>

	<Button class="mt-s" {loading} size="m" width="full" on:click={setWarmUpConfig}>
		{$format('button_save')}
	</Button>
{/if}
